import './DayPicker.scss';
import { CustomTooltip } from '@/components/custom-tooltip';
import { Label } from '@/components/ui/label';
import { Field, useField } from 'formik';
import { labels } from 'locales/en.label';
import { capitalize } from 'lodash';
import { useEffect } from 'react';
import { type DayOption, weekDays } from './config';

interface DayPickerProps {
	name: string;
	onValueChange?: (selectedDays: []) => void;
	validDays?: string[];
	isTooltipVisible?: boolean;
	isDisabled?: boolean;
}

export const DayPicker = ({
	name,
	onValueChange,
	isTooltipVisible = false,
	validDays = [],
	isDisabled = false,
}: DayPickerProps) => {
	const [field, meta] = useField(name);
	useEffect(() => {
		if (onValueChange) {
			onValueChange(field.value);
		}
	}, [field.value, onValueChange]);

	return (
		<>
			<fieldset className='day-picker'>
				{weekDays.map((day: DayOption, index) => {
					const isValidDay = validDays.includes(day.value);
					const isCheckboxDisabled =
						isDisabled || (isTooltipVisible && !isValidDay);

					const checkboxAndLabel = (
						<div key={index}>
							<Field
								className='day-picker__checkbox'
								id={day.value.toLowerCase()}
								type='checkbox'
								name={name}
								value={day.value}
								disabled={isCheckboxDisabled}
							/>
							<Label
								className='day-picker__label'
								htmlFor={day.value.toLowerCase()}
							>
								{day.label}
							</Label>
						</div>
					);

					if (!isTooltipVisible) return checkboxAndLabel;

					return (
						<CustomTooltip
							key={index}
							content={
								<p className='tw-text-foreground'>
									{!isValidDay
										? `${labels.daypicker.non_valid_days_tooltip_msg} ${capitalize(day.value)}s`
										: `${labels.daypicker.valid_days_tooltip_msg} ${capitalize(day.value)}s`}
								</p>
							}
							backgroundColor='tw-bg-white'
							arrowColor='tw-fill-primary'
							arrow
							borderColor='tw-border-primary'
						>
							{checkboxAndLabel}
						</CustomTooltip>
					);
				})}
			</fieldset>
			{meta.error && meta.touched && <p className='error'>{meta.error}</p>}
		</>
	);
};
